import { motion } from 'framer-motion';
import { RefreshCw, LayoutDashboard, Activity } from 'lucide-react';

interface DashboardHeroProps {
  title: string;
  subtitle: string;
  lastUpdated?: string;
  onRefresh?: () => void;
  refreshing?: boolean;
  variant?: 'manager' | 'head';
}

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 }
  }
};

const item = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 }
};

export default function DashboardHero({
  title,
  subtitle,
  lastUpdated,
  onRefresh,
  refreshing = false,
  variant = 'manager'
}: DashboardHeroProps) {
  const isManager = variant === 'manager';
  const gradientFrom = isManager ? 'from-cyan-600' : 'from-emerald-600';
  const gradientTo = isManager ? 'to-blue-700' : 'to-teal-700';
  const accentGlow = isManager ? 'bg-cyan-400/20' : 'bg-emerald-400/20';
  const Icon = isManager ? LayoutDashboard : Activity;

  return (
    <motion.header
      variants={container}
      initial="hidden"
      animate="visible"
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 shadow-xl"
    >
      {/* Background pattern and glow */}
      <div className="absolute inset-0 opacity-30">
        <div className={`absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl ${accentGlow}`} />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`
        }}
      />

      <div className="relative flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-6">
        <motion.div variants={item} className="flex items-center gap-4">
          <motion.div
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${gradientFrom} ${gradientTo} shadow-lg`}
            whileHover={{ scale: 1.05, rotate: 2 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            <Icon className="h-6 w-6 text-white" />
          </motion.div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-white sm:text-2xl">{title}</h1>
            <p className="mt-0.5 text-sm text-slate-400">{subtitle}</p>
          </div>
        </motion.div>

        <motion.div variants={item} className="flex flex-wrap items-center gap-3">
          {lastUpdated && (
            <span className="rounded-lg bg-white/5 px-3 py-1.5 text-xs text-slate-400 backdrop-blur-sm">
              Updated {new Date(lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <span className="hidden items-center gap-1.5 rounded-lg bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-400 sm:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </span>
          {onRefresh && (
            <motion.button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition hover:bg-white/20 disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </motion.button>
          )}
        </motion.div>
      </div>
    </motion.header>
  );
}
